/**
 * 文档 15：OrganizationController GET /organization/tree → 平台店铺结构
 */

import { toPlatformStoreId } from '@/utils/materialApiContext';

export function mapOrgDepartmentToPlatformStore(dto = {}, parent = null) {
  const departmentId = Number(dto.id);
  return {
    id: toPlatformStoreId(departmentId),
    name: dto.name || `店铺${departmentId}`,
    departmentId: Number.isFinite(departmentId) ? departmentId : undefined,
    parentId: dto.parentId != null ? Number(dto.parentId) : (parent?.id != null ? Number(parent.id) : undefined),
    groupName: parent?.name || '',
    groupId: parent?.id != null ? String(parent.id) : undefined,
    enabled: dto.enabled !== false,
    users: Array.isArray(dto.users) ? dto.users.map(mapOrgUser) : [],
  };
}

export function mapOrgUser(dto = {}) {
  return {
    id: dto.id != null ? String(dto.id) : '',
    name: dto.name || '',
    mobile: dto.mobile || '',
    email: dto.email || '',
    avatar: dto.avatar || '',
    role: dto.role || '',
    position: dto.position || '',
  };
}

/** 深度优先展开组织树为店铺/部门列表（即使根/分类节点自身为 enabled: false 也向下遍历其子树） */
export function flattenOrgDepartmentTree(root, acc = [], parent = null) {
  if (!root || typeof root !== 'object') return acc;

  const hasChildren = Array.isArray(root.children) && root.children.length > 0;
  const hasIdentity = root.id != null && String(root.name || '').trim();

  // 只有当自身有效(enabled !== false)且有标识时才作为可用店铺加入
  if (root.enabled !== false && hasIdentity) {
    acc.push(mapOrgDepartmentToPlatformStore(root, parent));
  }

  if (hasChildren) {
    root.children.forEach((child) => {
      flattenOrgDepartmentTree(child, acc, root);
    });
  }
  return acc;
}

/** 提取组织树中的叶子门店节点（具体业务店铺），并记录其所属上级团队/负责人 */
export function extractOrgLeafStores(root, acc = [], parent = null) {
  if (!root || typeof root !== 'object') return acc;

  const hasChildren = Array.isArray(root.children) && root.children.length > 0;
  const hasIdentity = root.id != null && String(root.name || '').trim();

  if (hasChildren) {
    root.children.forEach((child) => {
      // 若当前节点不是顶级根目录（如不是 74 或 34 店铺管理），则当前节点是子节点的分组/负责人
      const currentParent = (root.parentId != null || (root.id !== 74 && root.id !== 34)) ? root : parent;
      extractOrgLeafStores(child, acc, currentParent || root);
    });
  } else if (hasIdentity && root.enabled !== false) {
    acc.push(mapOrgDepartmentToPlatformStore(root, parent));
  }

  return acc;
}

/** 构建结构化的组织架构树（部门 -> 团队/负责人 -> 店铺） */
export function buildOrganizationHierarchy(root) {
  if (!root || typeof root !== 'object') {
    return { id: '74', name: '店铺管理', groups: [], totalStores: 0 };
  }
  const groups = [];
  let totalStores = 0;
  const children = Array.isArray(root.children) ? root.children : [];

  children.forEach((groupNode) => {
    const stores = extractOrgLeafStores(groupNode, [], groupNode);
    if (stores.length > 0) {
      groups.push({
        id: String(groupNode.id),
        name: groupNode.name || '默认团队',
        enabled: groupNode.enabled !== false,
        users: Array.isArray(groupNode.users) ? groupNode.users.map(mapOrgUser) : [],
        stores,
        storeCount: stores.length,
      });
      totalStores += stores.length;
    }
  });

  if (groups.length === 0) {
    const stores = extractOrgLeafStores(root);
    if (stores.length > 0) {
      groups.push({
        id: String(root.id || 'all'),
        name: root.name || '全部店铺',
        enabled: true,
        users: [],
        stores,
        storeCount: stores.length,
      });
      totalStores = stores.length;
    }
  }

  return {
    id: String(root.id || '74'),
    name: root.name || '店铺管理',
    enabled: root.enabled !== false,
    groups,
    totalStores,
  };
}

function normalizeUserId(user = {}) {
  const raw = user?.id ?? user?.userId;
  if (raw == null || String(raw).trim() === '') return null;
  const digits = String(raw).replace(/\D/g, '');
  return digits ? Number(digits) : null;
}

function departmentIncludesUser(department, userId) {
  if (!userId) return false;
  return (department.users || []).some(item => Number(item.id) === userId);
}

/**
 * 计算当前用户可切换的店铺 ID 列表（s1 形式）
 * - 优先匹配组织节点 users 中包含当前用户
 * - 平台管理员或未匹配时返回全部 enabled 店铺
 */
export function resolveAccessibleStoreIds(user = {}, departments = [], { allowAll = false } = {}) {
  const enabled = departments.filter(item => item.enabled !== false);
  if (!enabled.length) return [];

  const userId = normalizeUserId(user);
  if (userId && !allowAll) {
    const matched = enabled.filter(dept => departmentIncludesUser(dept, userId)).map(dept => dept.id);
    if (matched.length) return matched;
  }

  if (allowAll) return enabled.map(dept => dept.id);

  const legacy = Array.isArray(user.stores) ? user.stores.filter(id => enabled.some(dept => dept.id === id)) : [];
  if (legacy.length) return legacy;

  return enabled.map(dept => dept.id);
}

export function isPlatformAdminRole(roleKey) {
  return roleKey === 'platform_admin';
}

/** 仅 BOSS / SUPERADMIN 拥有全平台店铺访问权限 */
const FULL_ACCESS_BACKEND_ROLES = new Set(['BOSS', 'SUPERADMIN']);

/**
 * 判断后端角色列表是否包含全平台访问权限（BOSS 或 SUPERADMIN）
 * @param {string[]} roles - 后端返回的原始角色名列表
 */
export function hasFullStoreAccessRole(roles = []) {
  if (!Array.isArray(roles) || !roles.length) return false;
  return roles.some(r => FULL_ACCESS_BACKEND_ROLES.has(String(r).trim().toUpperCase().replace(/^ROLE_/, '')));
}
